import Paginated from "./paginated";
export default class PagedAsyncIterDriver<T> implements AsyncIterator<T[]> {
  private asyncIter: AsyncIterator<Paginated<T>>;
  public awaitingIterator = false;
  private _asyncPaginationState: AsyncPaginationState = {
    totalElements: 0,
    fetchedPages: 0,
    totalPages: 0,
    perPage: 0,
  };

  constructor(asyncIter: AsyncIterator<Paginated<T>>) {
    this.asyncIter = asyncIter;
  }

  public static fromAsyncIterable<T>(
    asyncIterable: AsyncIterable<Paginated<T>>
  ): PagedAsyncIterDriver<T> {
    return new PagedAsyncIterDriver<T>(asyncIterable[Symbol.asyncIterator]());
  }

  get asyncPaginationState(): AsyncPaginationState {
    return this._asyncPaginationState;
  }

  get fetchedElementsCount(): number {
    return (
      this._asyncPaginationState.fetchedPages *
      this._asyncPaginationState.perPage
    );
  }

  async next(): Promise<IteratorResult<T[]>> {
    this.awaitingIterator = true;
    let retVal: IteratorResult<T[]> = { done: true, value: [] };
    const step = await this.asyncIter.next();
    if (!step.done) {
      this._asyncPaginationState.fetchedPages += 1;
      this._asyncPaginationState.totalElements = step.value.total;
      this._asyncPaginationState.perPage = step.value.perPage;
      this._asyncPaginationState.totalPages = step.value.lastPage;
      retVal = { done: false, value: step.value.pageElements };
    }
    this.awaitingIterator = false;
    return retVal;
  }

  async fetchUntilFetchedCount(fetchQty: number): Promise<T[]> {
    const fetchElementsBuffer = [];
    const fetched = this.fetchedElementsCount;
    if (fetchQty > fetched) {
      for (
        let i = fetched;
        i < fetchQty;
        i += this._asyncPaginationState.perPage
      ) {
        const result = await this.next();
        fetchElementsBuffer.push(...result.value);
      }
    }
    return fetchElementsBuffer;
  }

  async fetchRemaining(): Promise<T[]> {
    const remainingBuffer = [];
    let done = false;
    while (!done) {
      const result = await this.next();
      done = !!result.done;
      if (!done) {
        remainingBuffer.push(...result.value);
      }
    }
    return remainingBuffer;
  }

  static fetchAll<A>(asyncIter: AsyncIterator<Paginated<A>>): Promise<A[]> {
    const driver = new PagedAsyncIterDriver(asyncIter);
    return driver.fetchRemaining();
  }
}

export interface AsyncPaginationState {
  totalElements: number;
  fetchedPages: number;
  totalPages: number;
  perPage: number;
}
